import React, { useState, useMemo } from 'react';
import { useActiveNavInView } from '@/shared/hooks/useActiveNavInView';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/core/auth/AuthContext';
import { useDynamicMenu } from '@/core/auth/useDynamicMenu';
import { styles } from './MainLayout.styles';
import * as LucideIcons from 'lucide-react';
import { HeaderSearch, NotificationBell } from '@/shared/components/header';

const RenderDynamicIcon: React.FC<{ name: string; size?: number; color?: string }> = ({ name, size = 17, color = 'currentColor' }) => {
  const IconComp = (LucideIcons as any)[name] || LucideIcons.Menu;
  return <IconComp size={size} color={color} />;
};

const Icon = ({ d, size = 17 }: { d: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const DEFAULT_CONSULTANCY_SECTIONS: { title: string; items: { title: string; path: string; iconName: string; sortOrder: number }[] }[] = [];

export const MainLayout = () => {
  const location  = useLocation();
  useActiveNavInView(location.pathname);
  const { user, logout } = useAuth();
  const { items: dynamicItems, loading: menuLoading } = useDynamicMenu();
  const [profileOpen, setProfileOpen] = useState(false);

  const initials = (user?.full_name || 'U').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const dynamicSections = useMemo(() => {
    if (dynamicItems.length === 0) return DEFAULT_CONSULTANCY_SECTIONS;
    const groups: Record<string, typeof dynamicItems> = {};
    for (const item of dynamicItems) {
      const cat = item.category || 'MAIN MENU';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    }
    return Object.entries(groups).map(([title, items]) => ({
      title,
      items: items.sort((a, b) => a.sortOrder - b.sortOrder)
    }));
  }, [dynamicItems]);


  return (
    <div style={styles.layoutWrapper}>
      {/* Sidebar */}
      <aside style={styles.sidebar}>
        {/* Brand */}
        <div style={styles.sidebarBrand}>
          <div style={styles.logoBox}>M</div>
          <div>
            <div style={styles.logoTextHeading}>Edu<span style={{ color: '#7367F0' }}>Global</span></div>
            <div style={styles.logoTextSub}>Consultancy Workspace</div>
          </div>
        </div>

        <nav className="sidebar-scroll" style={styles.navContainer}>
          {menuLoading ? (
            /* Loading skeleton — shows while dynamic menu API loads */
            <div style={{ padding: '12px 8px' }}>
              {[1,2,3,4,5,6,7].map(i => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 8px' }}>
                  <div style={{ width: 17, height: 17, borderRadius: 4, background: 'rgba(115,103,240,0.08)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  <div style={{ flex: 1, height: 12, borderRadius: 4, background: 'rgba(115,103,240,0.08)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                </div>
              ))}
            </div>
          ) : (
            dynamicSections.map(sec => (
              <React.Fragment key={sec.title}>
                <div className="nav-section-label" style={{ padding: '16px 8px 6px', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--clr-text-disabled)', letterSpacing: '0.05em' }}>{sec.title}</div>
                {sec.items.map(item => (
                  <Link key={item.path} to={item.path} className={`nav-item${location.pathname === item.path ? ' active' : ''}`}>
                    <RenderDynamicIcon name={item.iconName} size={17} />
                    <span>{item.title}</span>
                  </Link>
                ))}
              </React.Fragment>
            ))
          )}
        </nav>

        <button
          onClick={logout}
          className="nav-item"
          style={styles.signOutBtn}
        >
          <Icon d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4 M16 17l5-5-5-5 M21 12H9" />
          Sign Out
        </button>
      </aside>


      {/* Main Content */}
      <div style={styles.mainArea}>
        {/* Header */}
        <header style={styles.header}>
          <div style={styles.headerTitle}>
            Consultancy Workspace
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <HeaderSearch placeholder="Search students, applications, leads…" width={300} />
          <NotificationBell />
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setProfileOpen(p => !p)}
              style={styles.profileBtn}
            >
              <div className="avatar avatar-md" style={styles.profileAvatar}>{initials}</div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4C4E6F' }}>
                  {user?.full_name || 'Admin'}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#9092A9' }}>
                  {user?.role || 'admin'}
                </div>
              </div>
            </button>

            {profileOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 149 }} onClick={() => setProfileOpen(false)} onKeyDown={(e) => { if (e.key === 'Enter') setProfileOpen(false); }} role="presentation" />
                <div className="dropdown-menu" style={styles.dropdownMenu}>
                  <div style={styles.dropdownUserHeader}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4C4E6F' }}>{user?.full_name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#9092A9' }}>{user?.email}</div>
                  </div>
                  <Link to="/profile" className="dropdown-item" onClick={() => setProfileOpen(false)}>My Profile</Link>
                  <Link to="/settings" className="dropdown-item" onClick={() => setProfileOpen(false)}>Workspace Settings</Link>
                  <div style={{ borderTop: '1px solid var(--clr-border-light)', margin: '4px 0' }} />
                  <button className="dropdown-item danger" onClick={logout}>Sign Out</button>
                </div>
              </>
            )}
          </div>
          </div>
        </header>

        {/* Content */}
        <main style={styles.mainContent} className="page-enter">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
